import type { ApiErrorBody } from "@csbi/contracts"

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message || body.error)
    this.name = "ApiError"
  }
}

const BASE = "/api"

export async function request<T>(
  path: string,
  init: RequestInit & { signal?: AbortSignal } = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null
    throw new ApiError(res.status, body ?? { error: "unknown", message: `Request failed (HTTP ${res.status})` })
  }
  // A 204 has no body and `res.json()` throws — a delete endpoint would become "the server succeeded and the frontend reported an error"
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const get = <T>(path: string, signal?: AbortSignal) =>
  request<T>(path, signal ? { signal } : {})

export const post = <T>(path: string, body: unknown, signal?: AbortSignal) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body), ...(signal ? { signal } : {}) })

export const patch = <T>(path: string, body: unknown, signal?: AbortSignal) =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body), ...(signal ? { signal } : {}) })

export const put = <T>(path: string, body: unknown, signal?: AbortSignal) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body), ...(signal ? { signal } : {}) })

export const del = <T = void>(path: string, signal?: AbortSignal) =>
  request<T>(path, { method: "DELETE", ...(signal ? { signal } : {}) })
